// @ts-nocheck

import React, { FC, useEffect, useState, useRef, useContext } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  GameHeader,
  ExampleImagesSection,
  TextFieldsSection,
  ImagesSection,
  MusicSection,
  ActionsSection,
  ResultsSection,
  GameExpiredOverlay,
  BrowserErrorModal,
  processFormData,
  FileDownloadSection,
} from 'src/shared/components/Constructor';
import { QRCodeGenerator } from 'src/shared/components';
import { FormValues, TextFieldConfig, ImageFieldConfig } from 'src/shared/components/Constructor/types';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';
import { Container, Grid, Button, Typography, Stack, Box, TextField, Accordion, AccordionSummary, AccordionDetails, IconButton, Tooltip } from '@mui/material';

import { RealProgress } from '@shared/components/Constructor/GameCreationProgress';
import { MobileCropper } from '@shared/components/ImageCropper';
import type { GameKey } from '@shared/types';
import { isGameExpired, getEndEditDate } from '@shared/utils/gameExpiration';
import { loadImageWithFallback } from '@shared/utils/image-loader';
import { MP3Compressor } from '@shared/utils/mp3-compression';
import { getBrowserDiagnostics } from '@shared/utils/browser-detection';
import { audioBufferToWav } from '@shared/utils/audio-utils';

import { authContext } from '@store/index';
import RetryImg from '@shared/components/RetryImg';

import * as ClientActions from './client.actions';
import { getIsClientFormDisabled } from './client.utils';

import styles from './client.module.scss';

interface ClientPageProps {
  gameKey: GameKey;
}

// Конфигурация полей для ClientSlot
const textFields: TextFieldConfig[] = [
  { name: 'nameGameHeader', label: 'clientSlot.textNameGame', placeholder: 'clientSlot.textNameGamePlaceholder', tooltip: 'clientSlot.textNameGameTooltip', required: false },
  { name: 'startGameHeader', label: 'clientSlot.textStartGame', placeholder: 'clientSlot.textStartGamePlaceholder', tooltip: 'clientSlot.textStartGameTooltip', required: false },
  { name: 'moneyText', label: 'clientSlot.textMoney', placeholder: 'clientSlot.textMoneyPlaceholder', tooltip: 'clientSlot.textMoneyTooltip', required: false },
  { name: 'endGameHeader', label: 'clientSlot.textEndGame', placeholder: 'clientSlot.textEndGamePlaceholder', tooltip: 'clientSlot.textEndGameTooltip', required: false },
];

const imageFields: ImageFieldConfig[] = [
  { name: 'startGameImage', label: 'clientSlot.selectStartImage', aspectRatio: 1, tooltip: 'clientSlot.selectStartImageTooltip', required: false },
  { name: 'endGameImage', label: 'clientSlot.selectEndImage', aspectRatio: 1, tooltip: 'clientSlot.selectEndImageTooltip', required: false },
  { name: 'megaWinImage', label: 'clientSlot.selectMegaWinImage', aspectRatio: 1, tooltip: 'clientSlot.selectMegaWinImageTooltip', required: false },
];

// Конфигурация для 14 игровых плиток (для слотов)
const gameTilesFields: ImageFieldConfig[] = Array.from({ length: 14 }, (_, index) => ({
  name: `gameTiles.${index}`,
  label: `${index + 1}`,
  aspectRatio: 1 / 1,
  tooltip: `clientSlot.gameTile${index + 1}Tooltip`,
  required: false,
  gridProps: { xs: 12, sm: 6 },
}));

// Кастомный компонент для текстовых полей слотов с примерами
const SlotTextFieldsSection: FC<{
  control: any;
  errors: any;
  fields: TextFieldConfig[];
  isClientFormDisabled: boolean;
}> = ({ control, errors, fields, isClientFormDisabled }) => {
  const { t } = useTranslation();

  const exampleImages = {
    nameGameHeader: "https://eatsy-games-defaults-prod.s3.eu-central-1.amazonaws.com/images/Games/Slot001/gameName.png",
    startGameHeader: "https://eatsy-games-defaults-prod.s3.eu-central-1.amazonaws.com/images/Games/Slot001/anyWinText.png",
    moneyText: "https://eatsy-games-defaults-prod.s3.eu-central-1.amazonaws.com/images/Games/Slot001/moneyText.png",
    endGameHeader: "https://eatsy-games-defaults-prod.s3.eu-central-1.amazonaws.com/images/Games/Slot001/4spinTxt.png",
    urlButton: "https://eatsy-games-defaults-prod.s3.eu-central-1.amazonaws.com/images/Games/Slot001/urlButtonText.png",
  };

  return (
    <Grid container spacing={3}>
      {fields.map((field) => (
        <Grid key={field.name} item xs={12} sm={8} md={6}>
          <Controller
            name={field.name as keyof FormValues}
            control={control}
            rules={{ required: field.required }}
            render={({ field: controllerField }) => (
              <TextField
                {...controllerField}
                disabled={isClientFormDisabled}
                error={!!errors?.[field.name as keyof FormValues]}
                fullWidth
                label={t(field.label)}
                placeholder={field.placeholder ? t(field.placeholder) : undefined}
                sx={{
                  '& .MuiInputBase-root': {
                    paddingRight: '40px', // Место для иконки
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      title={field.tooltip ? t(field.tooltip) : ''}
                    >
                      <IconButton 
                        size="small"
                        sx={{ 
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                        }}
                      >
                        <HelpIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
              />
            )}
          />
          <Accordion sx={{ mt: 1, boxShadow: 'none', border: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`example-image-content-${field.name}`}
              id={`example-image-header-${field.name}`}
            >
              <Typography
                align="center"
                sx={{
                  width: '100%',
                  border: '1.5px solid #e196a0',
                  borderRadius: '8px',
                  padding: '4px 0',
                  color: '#e196a0',
                  fontWeight: 500,
                  fontSize: '1rem',
                  background: 'rgba(225,150,160,0.05)'
                }}
              >
                {t('clientAll.example')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <RetryImg
                  src={exampleImages[field.name as keyof typeof exampleImages]}
                  alt="Пример"
                  maxRetries={3}
                  retryDelayMs={1000}
                  style={{
                    width: '100%',
                    maxWidth: '270px',
                    aspectRatio: '9/16',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    objectFit: 'cover'
                  }}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Grid>
      ))}
    </Grid>
  );
};

const ClientPageSlot: FC<ClientPageProps> = ({ gameKey: initialGameKey }) => {
  useEffect(() => {
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  }, []);

  const { t } = useTranslation();
  const [authData] = useContext(authContext);

  const [gameKey, setGameKey] = useState(initialGameKey);
  const { gameBuild: alreadyExistedGameBuild } = gameKey;
  const [_, setActivateGameDate] = ClientActions.useSetActivateGameDate();
  const gameDefaults = ClientActions.useFetchGameDefaults(gameKey.gameName);

  const [existingGameBuild, fetchGameBuild, gameBuildError] = ClientActions.useFetchGameBuild();

  const [createdGameBuild, handleGameBuildSubmit, gameBuildCreationLoading] =
    ClientActions.useCreateGameBuild();
  const [trimmedAudioFile, setTrimmedAudioFile] = useState<File | null>(null);
  const croppedPixelsDataRef = useRef<{ [key: string]: any }>({});
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState('');
  const [stepProgress, setStepProgress] = useState(0);
  const [showBrowserError, setShowBrowserError] = useState(false);

  // ВАЖНО: объявляем gameBuild до всех useEffect!
  const gameBuild = createdGameBuild ?? existingGameBuild ?? alreadyExistedGameBuild;
  const showGameLink = Boolean(gameBuild && gameBuild._id);
  
  // Отладочная информация
  // console.log('Current gameBuild:', gameBuild);
  // console.log('Current createdGameBuild:', createdGameBuild);
  // console.log('Current alreadyExistedGameBuild:', alreadyExistedGameBuild);

  // Проверка истекшей игры
  const [isGameExpiredState, setIsGameExpiredState] = useState(false);

  useEffect(() => {
    // Проверяем, истекла ли игра
    const expired = isGameExpired(gameKey.activateDate, gameKey.gameDayCount);
    setIsGameExpiredState(expired);
  }, [gameKey.activateDate, gameKey.gameDayCount]);

  // Определяем режим только для чтения
  const isReadOnly = authData?.isReadOnly || false;

  const {
    handleSubmit,
    formState: { errors },
    setError,
    control,
    reset,
    getValues,
    setValue,
    trigger,
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      ...textFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      ...imageFields.reduce((acc, field) => ({ ...acc, [field.name]: '' }), {}),
      customMusicFile: '',
      urlButton: '',
      fileDownloadUrl: '',
      language: 'English',
      gameTiles: Array(14).fill(''),
    },
    mode: 'onSubmit',
  });

  // Загружаем существующую игру при наличии gameBuildId
  useEffect(() => {
    if (gameKey.gameBuildId) {
      fetchGameBuild(gameKey.gameBuildId);
    }
  }, [gameKey.gameBuildId, fetchGameBuild]);

  // Загружаем данные в форму при получении/обновлении gameBuild
  useEffect(() => {
    if (gameBuild) {
      console.log('Loading gameBuild data into form:', gameBuild);
      const newValues: any = {};
      
      // Заполняем текстовые поля
      textFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          nameGameHeader: 'nameGameHeader',
          startGameHeader: 'startGameHeader',
          moneyText: 'moneyText',
          endGameHeader: 'endGameHeader',
          urlButton: 'urlButton',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          const value = gameBuild[buildKey] || '';
          console.log(`Setting ${field.name} to:`, value);
          newValues[field.name] = value;
        }
      });
      
      // Заполняем поля изображений
      imageFields.forEach(field => {
        const keyMap: Record<string, keyof typeof gameBuild> = {
          startGameImage: 'startGameImageUrl',
          endGameImage: 'endGameImageUrl',
          megaWinImage: 'moneyWinImageUrl',
        };
        const buildKey = keyMap[field.name];
        if (buildKey) {
          const value = gameBuild[buildKey] || '';
          console.log(`Setting ${field.name} to:`, value);
          // Если это URL изображения, оставляем как есть
          newValues[field.name] = value;
          console.log(`Final ${field.name} value:`, newValues[field.name]);
        }
      });
      
      // Заполняем игровые плитки
      const gameTilesUrls = gameBuild.gameTilesUrls || [];
      // Убеждаемся, что у нас всегда 14 элементов
      newValues.gameTiles = Array(14).fill('').map((_, index) => gameTilesUrls[index] || '');
      console.log('Setting gameTiles to:', newValues.gameTiles);
      console.log('gameBuild.gameTilesUrls:', gameBuild.gameTilesUrls);
      
      // Проверяем, что изображения загружаются
      if (gameBuild.gameTilesUrls) {
        // Используем новую утилиту для безопасной загрузки изображений
        Promise.allSettled(
          gameBuild.gameTilesUrls.map((url, index) => 
            loadImageWithFallback(url)
              .then(() => console.log(`✅ Image ${index} loaded successfully:`, url))
              .catch(error => console.error(`❌ Image ${index} failed to load:`, url, error))
          )
        ).catch(error => {
          console.error('Error loading images:', error);
        });
      }
      
      // Дополнительные поля
      console.log('gameBuild music field:', gameBuild.musicUrl);
      console.log('gameBuild all fields:', Object.keys(gameBuild));
      console.log('Full gameBuild object:', gameBuild);
      newValues.customMusicFile = gameBuild.musicUrl || '';
      // Разделяем: urlButton для URL, fileDownloadUrl для файла
      // Используем пустые строки вместо null для избежания предупреждений React
      newValues.urlButton = gameBuild.urlButton || '';
      newValues.fileDownloadUrl = gameBuild.fileDownloadUrl || '';
      newValues.language = gameBuild?.language || 'English';
      console.log('Setting customMusicFile to:', newValues.customMusicFile);
      console.log('Setting fileDownload to:', newValues.fileDownload);
      console.log('Setting language to:', newValues.language);
      
      console.log('Setting form values:', newValues);
      reset(newValues);
    } else if (gameDefaults) {
      reset({
        customMusicFile: gameDefaults.defaultMusicUrl || '',
        language: 'English',
        gameTiles: Array(14).fill(''),
      });
    }
  }, [gameBuild, reset, gameDefaults?._id]);




  const onSubmit = async (data: FormValues) => {
    try {
      console.log('Submitting form with data:', data);
      setShowProgress(true);
      setCurrentStep('validation');
      setStepProgress(0);

      // Для слотов проверки обязательности картинок отключены по требованиям

      // Валидация
      setCurrentStep('validation');
      setStepProgress(50);
      await new Promise(resolve => setTimeout(resolve, 500));
      setStepProgress(100);

      // Обработка музыки
      setCurrentStep('music');
      setStepProgress(0);
      let audioFileToUse = data.customMusicFile;
      
      console.log('Processing music - customMusicFile:', data.customMusicFile);
      console.log('Processing music - trimmedAudioFile:', trimmedAudioFile);
      
      // Если есть обрезанный файл, используем его
      if (trimmedAudioFile) {
        console.log('Using trimmed audio file:', trimmedAudioFile);
        audioFileToUse = trimmedAudioFile;
      } else if (data.customMusicFile instanceof File) {
        setStepProgress(10);
        console.log('Запускаем автоматическую обрезку и компрессию музыки...');
        
        try {
          // Создаем AudioContext для обрезки
          const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          
          // Читаем файл
          const arrayBuffer = await data.customMusicFile.arrayBuffer();
          const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
          
          console.log('Аудио декодировано, длительность:', audioBuffer.duration, 'сек');
          
          // Используем полную длительность аудио без автоматической обрезки
          const startTime = 0;
          const endTime = audioBuffer.duration;
          
          console.log('Используем полную длительность аудио:', { startTime, endTime, duration: audioBuffer.duration });
          
          // Вычисляем сэмплы для обрезки
          const sampleRate = audioBuffer.sampleRate;
          const startSample = Math.floor(startTime * sampleRate);
          const endSample = Math.floor(endTime * sampleRate);
          const trimmedLength = endSample - startSample;
          
          // Создаем новый AudioBuffer для обрезанного аудио
          const trimmedBuffer = audioContext.createBuffer(
            audioBuffer.numberOfChannels,
            trimmedLength,
            sampleRate
          );
          
          // Копируем данные из выбранного диапазона
          for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
            const originalData = audioBuffer.getChannelData(channel);
            const trimmedData = trimmedBuffer.getChannelData(channel);
            
            for (let i = 0; i < trimmedLength; i++) {
              trimmedData[i] = originalData[startSample + i];
            }
          }
          
          // Конвертируем AudioBuffer в MP3 используя MP3Compressor
          const compressor = new MP3Compressor();
          
          // Сохраняем оригинальное название файла
          const originalFileName = data.customMusicFile.name;
          const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
          
          // Создаем WAV файл из AudioBuffer
          const wavBlob = await audioBufferToWav(trimmedBuffer);
          const tempFile = new File([wavBlob], `${fileNameWithoutExt}`, { type: 'audio/wav' });
          
          const compressionResult = await compressor.compressMP3(tempFile);
          const trimmedFile = compressionResult.compressedFile;
          
          console.log('Автоматическая обрезка завершена:', trimmedFile.name, 'размер:', trimmedFile.size);
          
          audioFileToUse = trimmedFile;
          // Проверяем состояние AudioContext перед закрытием
          if (audioContext.state !== 'closed') {
            try {
              audioContext.close();
            } catch (error) {
              console.error('Ошибка при закрытии AudioContext:', error);
            }
          }
          
        } catch (error) {
          console.error('Ошибка автоматической обрезки музыки:', error);
          // Если обрезка не удалась, используем оригинальный файл
          audioFileToUse = data.customMusicFile;
        }
      }
      setStepProgress(100);

      // Обработка изображений
      setCurrentStep('images');
      setStepProgress(0);
      // Используем все tiles, включая пустые, чтобы сохранить позиции
      const tiles: Array<string | File> = data.gameTiles || Array(14).fill('');
      console.log('Tiles before submission:', tiles);
      setStepProgress(100);

      // Загрузка
      setCurrentStep('upload');
      setStepProgress(0);
      console.log('Music processing - audioFileToUse:', audioFileToUse);
      console.log('Music processing - trimmedAudioFile:', trimmedAudioFile);
      
      // Создание игры
      setCurrentStep('creation');
      setStepProgress(0);
      try {
        console.log('Submitting with audioFileToUse:', audioFileToUse);
        
        await handleGameBuildSubmit(
          { 
            _id: gameKey.gameBuildId, 
            ...data, 
            customMusicFile: audioFileToUse,
            fileDownload: data.fileDownload
          },
          croppedPixelsDataRef.current,
          tiles
        );
        // Подтягиваем актуальные данные билда сразу после успешной отправки
        if (gameKey.gameBuildId) {
          fetchGameBuild(gameKey.gameBuildId);
        }
        setStepProgress(100);
      } catch (error) {
        console.error('Error in handleGameBuildSubmit:', error);
        throw error;
      }

      // Дальнейшая обработка выполнится в useEffect по createdGameBuild

      setCurrentStep('complete');
      setStepProgress(100);
    } catch (error) {
      console.error('Error submitting form:', error);
      setError('root', { message: 'Ошибка при сохранении игры' });
      setShowProgress(false);
    }
  };

  const handleProgressComplete = () => {
    setShowProgress(false);
    setCurrentStep('');
    setStepProgress(0);
  };

  // Реагируем на успешное создание билда и обновляем состояние/перезагружаем билд
  useEffect(() => {
    if (createdGameBuild && createdGameBuild._id) {
      const result = createdGameBuild;
      console.log('Game build created successfully (effect):', result);
      setGameKey((prev) => ({
        ...prev,
        gameBuild: result,
        gameBuildId: result._id,
      }));
      // Загружаем свежую версию билда
      setTimeout(() => {
        console.log('Fetching updated game build with ID (effect):', result._id);
        fetchGameBuild(result._id);
      }, 100);

      // Активируем игру при первом сохранении
      if (!gameKey.activateDate) {
        setActivateGameDate(gameKey._id);
        setGameKey((prev) => ({
          ...prev,
          activateDate: new Date(),
        }));
      }
    }
  }, [createdGameBuild?._id]);

  const onError = (errors: any) => {
    const missingFields = [
      ...textFields.filter(f => f.required && errors[f.name]),
      ...imageFields.filter(f => f.required && errors[f.name]),
      ...gameTilesFields.filter(f => f.required && errors[f.name]),
    ];
    if (missingFields.length > 0) {
      const fieldsList = missingFields.map(({ label }) => `📝 ${t(label)}`).join('\n');
      const errorMessage = `${t('clientAll.validation.requiredFieldsMessage')}\n\n${fieldsList}`;
      setError('root', {
        message: errorMessage
      });
    }
  };

  const handleTestGameClick = () => {
    if (gameBuild && gameBuild._id) {
      window.open(`/game?buildId=${gameBuild._id}`, '_blank');
    }
  };

  const handleUploadMultipleImages = async (event) => {
    const uploadedFiles = Array.from(event?.target?.files || []);
    if (uploadedFiles.length > 17) {
      alert('Максимальное количество изображений - 17');
      return;
    }

    const newFiles = Array(14).fill(null);
    uploadedFiles.slice(3).forEach((file, index) => {
      if (index < 14 && file instanceof File) {
        newFiles[index] = file;
      }
    });

    console.log('New files array:', newFiles);

    // Устанавливаем значения для тайлов
    newFiles.forEach((file, index) => {
      console.log(`Setting gameTiles.${index}:`, file);
      if (file instanceof File) {
        setValue(`gameTiles.${index}`, file);
      }
    });

    // Устанавливаем значения для основных изображений
    if (uploadedFiles[0] instanceof File) {
      console.log('Setting startGameImage:', uploadedFiles[0]);
      setValue('startGameImage', uploadedFiles[0]);
    }
    if (uploadedFiles[1] instanceof File) {
      console.log('Setting endGameImage:', uploadedFiles[1]);
      setValue('endGameImage', uploadedFiles[1]);
    }
    if (uploadedFiles[2] instanceof File) {
      console.log('Setting megaWinImage:', uploadedFiles[2]);
      setValue('megaWinImage', uploadedFiles[2]);
    }

    // Проверяем, что значения установлены
    console.log('Form values after upload:', getValues());

    // Проверяем, были ли загружены изображения
    setTimeout(() => {
      const currentValues = getValues();
      const hasStartImage = currentValues.startGameImage instanceof File;
      const hasEndImage = currentValues.endGameImage instanceof File;
      const hasMegaWinImage = currentValues.megaWinImage instanceof File;
      const hasGameTiles = currentValues.gameTiles?.some(tile => tile instanceof File);
      
      // Если изображения не загрузились, показываем ошибку браузера
      if (!hasStartImage && !hasEndImage && !hasMegaWinImage && !hasGameTiles) {
        const diagnostics = getBrowserDiagnostics();
        if (diagnostics.browser.isEmbedded || !diagnostics.isCompatible) {
          setShowBrowserError(true);
        }
      }
    }, 100);
  };

  if (!gameDefaults) {
    return null;
  }

  const { activateDate, editDayCount, gameDayCount } = gameKey;

  const isClientFormDisabled = getIsClientFormDisabled(
    activateDate,
    editDayCount
  ) || isReadOnly || isGameExpiredState;

  return (
    <Container maxWidth={false} className={styles.gameContainer} sx={{ maxWidth: '900px' }}>
      <GameHeader
        gameDefaults={gameDefaults}
        activateDate={activateDate ? (typeof activateDate === 'string' ? new Date(activateDate) : activateDate) : null}
        editDayCount={editDayCount}
        gameDayCount={gameDayCount}
      />

      <ExampleImagesSection gameDefaults={gameDefaults} />

      {/* Компонент прогресса создания игры */}
      {showProgress && (
        <RealProgress
          isVisible={showProgress}
          onComplete={handleProgressComplete}
          currentStep={currentStep}
          stepProgress={stepProgress}
        />
      )}
      
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {/* Секция множественной загрузки изображений */}
        <Grid container item xs={12} className={styles.justifyCenter}>
          <Grid
            item
            xs={12}
            className={`${styles.justifyCenter} flex`}
            style={{ marginTop: '20px' }}
          >
            <Stack
              className={`${styles.justifyCenter} tooltipWrapperInline`}
              direction="row"
            >
              <Typography variant="h6" noWrap>
                {t('clientSlot.selectMultipleImages')}
              </Typography>
            </Stack>
          </Grid>

          <Grid
            item
            xs={12}
            style={{ marginBottom: '40px', marginTop: '10px' }}
          >
            <Grid container justifyContent="center">
              <Button
                className={styles.uploadMultipleButton}
                variant="contained"
                component="label"
                disabled={isReadOnly}
              >
                {t('clientSlot.uploadMultiple')}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  hidden
                  onChange={handleUploadMultipleImages}
                  disabled={isReadOnly}
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>

        {/* Текстовые поля со спойлерами */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" className={styles.sectionTitle} sx={{ mb: 3 }}>
            {t('clientSlot.textSectionTitle')}
          </Typography>
          <SlotTextFieldsSection 
            control={control} 
            errors={errors} 
            fields={textFields} 
            isClientFormDisabled={isClientFormDisabled} 
          />
        </Box>

        {/* Общий блок с основными элементами */}
        <Grid container spacing={3} style={{ marginBottom: '2rem' }}>
          {/* Блок 1: Начальный текст + Изображение на заставку */}
          <Grid item xs={12} md={6}>
            <Box className={styles.formSection}>
              <Typography variant="h5" className={styles.sectionTitle}>
                {t('clientSlot.gameInfo')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="startGameImage"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                  <MobileCropper
                        label={t('clientSlot.selectStartImage')}
                        value={field.value}
                        onChange={field.onChange}
                        aspectRatio={1}
                        isDisabled={isClientFormDisabled}
                        maskImage={Array.isArray(gameDefaults.maskImageUrl)
                          ? gameDefaults.maskImageUrl[0] || ''
                          : gameDefaults.maskImageUrl || ''}
                        toolTipContent={t('clientSlot.selectStartImageTooltip')}
                        fieldName="startGameImage"
                        onCroppedPixelsChange={(fieldName, croppedPixels) => {
                          croppedPixelsDataRef.current[fieldName] = croppedPixels;
                        }}
                        hasError={!!errors?.startGameImage}
                        errorMessage={(errors?.startGameImage as any)?.message as string}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Блок 2: Финальное сообщение + Финальное изображение */}
          <Grid item xs={12} md={6}>
            <Box className={styles.formSection}>
              <Typography variant="h5" className={styles.sectionTitle}>
                {t('clientSlot.finalContent')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="endGameImage"
                    control={control}
                    rules={{ required: false }}
                    render={({ field }) => (
                  <MobileCropper
                        label={t('clientSlot.selectEndImage')}
                        value={field.value}
                        onChange={field.onChange}
                        aspectRatio={1}
                        isDisabled={isClientFormDisabled}
                        maskImage={Array.isArray(gameDefaults.maskImageUrl)
                          ? gameDefaults.maskImageUrl[1] ||
                            gameDefaults.maskImageUrl[0] ||
                            ''
                          : gameDefaults.maskImageUrl || ''}
                        toolTipContent={t('clientSlot.selectEndImageTooltip')}
                        fieldName="endGameImage"
                        onCroppedPixelsChange={(fieldName, croppedPixels) => {
                          croppedPixelsDataRef.current[fieldName] = croppedPixels;
                        }}
                        hasError={!!errors?.endGameImage}
                        errorMessage={(errors?.endGameImage as any)?.message as string}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Блок 3: Мега-победа */}
          <Grid item xs={12} md={6} style={{ margin: '0 auto' }}>
            <Box className={styles.formSection} sx={{ textAlign: 'center' }}>
              <Typography variant="h5" className={styles.sectionTitle}>
                {t('clientSlot.megaWinSection')}
              </Typography>
              <Controller
                name="megaWinImage"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <MobileCropper
                    label={t('clientSlot.selectMegaWinImage')}
                    value={field.value}
                    onChange={field.onChange}
                    aspectRatio={1}
                    isDisabled={isClientFormDisabled}
                    maskImage={Array.isArray(gameDefaults.maskImageUrl)
                      ? gameDefaults.maskImageUrl[2] ||
                        gameDefaults.maskImageUrl[0] ||
                        ''
                      : gameDefaults.maskImageUrl || ''}
                    toolTipContent={t('clientSlot.selectMegaWinImageTooltip')}
                    fieldName="megaWinImage"
                    onCroppedPixelsChange={(fieldName, croppedPixels) => {
                      croppedPixelsDataRef.current[fieldName] = croppedPixels;
                    }}
                    hasError={!!errors?.megaWinImage}
                    errorMessage={(errors?.megaWinImage as any)?.message as string}
                  />
                )}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Секция с 14 карточками для игровых плиток */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <ImagesSection 
            control={control} 
            errors={errors} 
            isReadOnly={isReadOnly} 
            cropDataRef={croppedPixelsDataRef} 
            gameType="slot"
            images={gameTilesFields.map((field) => ({
              ...field,
              // maskImage: Array.isArray(gameDefaults.maskImageUrl)
              //   ? gameDefaults.maskImageUrl[2] ||
              //     gameDefaults.maskImageUrl[0] ||
              //     ''
              //   : gameDefaults.maskImageUrl || '',
            }))}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <MusicSection
            control={control}
            errors={errors}
            isReadOnly={isReadOnly}
            setError={setError}
            onTrimmedFileChange={setTrimmedAudioFile}
            musicFieldName="customMusicFile"
            waveformTrimmerFieldName="customMusicFile"
          />
        </Box>

        {/* Блок загрузки файла */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <FileDownloadSection
            control={control}
            errors={errors}
            isReadOnly={isReadOnly}
            setError={setError}
            setValue={setValue}
            getValues={getValues}
            fieldName="urlButton"
          />
        </Box>

        <ActionsSection
          gameBuild={gameBuild}
          gameBuildCreationLoading={gameBuildCreationLoading}
          isReadOnly={isReadOnly}
          onTestGameClick={handleTestGameClick}
          hasErrors={!!errors.root || Object.keys(errors).length > 0}
        />

        <ResultsSection
          errors={errors}
          gameBuild={gameBuild}
          showGameLink={showGameLink}
        />

        {/* QR-код компонент */}
        <QRCodeGenerator
          gameUrl={gameBuild ? `${window.location.origin}/game?buildId=${gameBuild._id}` : ''}
          gameBuildId={gameBuild?._id}
          showGameLink={showGameLink}
        />
      </form>

      {/* Оверлей с уведомлением об истечении срока */}
      <GameExpiredOverlay isVisible={isGameExpiredState} />
      
      {/* Модальное окно с ошибкой браузера */}
      <BrowserErrorModal 
        isOpen={showBrowserError} 
        onClose={() => setShowBrowserError(false)}
        gameType="slot"
      />
    </Container>
  );
};

export default ClientPageSlot;
